import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as mnPowerApi from '../api/mnPower';

export function MnPower() {
  const navigate = useNavigate();
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canViewReports = hasPermission('reports:read');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canViewReports) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const cfg = await mnPowerApi.getMnPowerConfig(selectedOrg.id);
      setConfig(cfg);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canViewReports]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (!canViewReports) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need reports permissions to view MN Power module.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading MN Power module...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={styles.container}>
        <PageHeader
          title="MN Power Program"
          subtitle="Minnesota Power EV Program"
        />
        <div style={styles.notEnabled}>
          <h3>Module Not Available</h3>
          <p>The MN Power module is not enabled for your organization.</p>
          <p>Contact your administrator to enable this feature.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title={config.programName}
        subtitle={config.description}
      />

      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('overview')}
          style={activeTab === 'overview' ? styles.activeTab : styles.tab}
          data-testid="tab-overview"
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('fields')}
          style={activeTab === 'fields' ? styles.activeTab : styles.tab}
          data-testid="tab-fields"
        >
          Fields
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          style={activeTab === 'templates' ? styles.activeTab : styles.tab}
          data-testid="tab-templates"
        >
          Templates
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          style={activeTab === 'reports' ? styles.activeTab : styles.tab}
          data-testid="tab-reports"
        >
          Reports
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {activeTab === 'overview' && (
        <div style={styles.tabContent} data-testid="content-overview">
          <h3 style={styles.sectionTitle}>Quick Actions</h3>
          <div style={styles.shortcutsGrid}>
            {config.shortcuts.map(shortcut => (
              <div
                key={shortcut.route}
                style={styles.shortcutCard}
                onClick={() => navigate(shortcut.route)}
                data-testid={`shortcut-${shortcut.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <span style={styles.shortcutLabel}>{shortcut.label}</span>
              </div>
            ))}
          </div>

          <h3 style={styles.sectionTitle}>Program Summary</h3>
          <div style={styles.summaryGrid}>
            <div style={styles.summaryCard}>
              <span style={styles.summaryValue}>{config.fieldDefinitions.length}</span>
              <span style={styles.summaryLabel}>Custom Fields</span>
            </div>
            <div style={styles.summaryCard}>
              <span style={styles.summaryValue}>{config.templates.length}</span>
              <span style={styles.summaryLabel}>Templates</span>
            </div>
            <div style={styles.summaryCard}>
              <span style={styles.summaryValue}>{config.reportProfiles.length}</span>
              <span style={styles.summaryLabel}>Report Profiles</span>
            </div>
            <div style={styles.summaryCard}>
              <span style={styles.summaryValue}>{config.validationRules.length}</span>
              <span style={styles.summaryLabel}>Validation Rules</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'fields' && (
        <div style={styles.tabContent} data-testid="content-fields">
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Field Key</th>
                  <th style={styles.th}>Label</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Required</th>
                  <th style={styles.th}>Validation</th>
                </tr>
              </thead>
              <tbody>
                {config.fieldDefinitions.map(field => (
                  <tr key={field.key} style={styles.tr}>
                    <td style={styles.td}><code style={styles.code}>{field.key}</code></td>
                    <td style={styles.td}>{field.label}</td>
                    <td style={styles.td}>
                      <span style={styles.typeBadge}>{field.type}</span>
                    </td>
                    <td style={styles.td}>
                      {field.required ? (
                        <span style={styles.requiredBadge}>Required</span>
                      ) : (
                        <span style={styles.optionalBadge}>Optional</span>
                      )}
                    </td>
                    <td style={styles.td}>
                      {field.validation ? (
                        <code style={styles.code}>{JSON.stringify(field.validation)}</code>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'templates' && (
        <div style={styles.tabContent} data-testid="content-templates">
          <div style={styles.cardsGrid}>
            {config.templates.map(template => (
              <div key={template.key} style={styles.templateCard}>
                <h4 style={styles.templateName}>{template.name}</h4>
                <div style={styles.templateMeta}>
                  <span style={styles.typeBadge}>{template.type}</span>
                  <span style={styles.formatBadge}>{template.format}</span>
                </div>
                {template.columns && (
                  <div style={styles.columns}>
                    <strong>Columns:</strong> {template.columns.join(', ')}
                  </div>
                )}
                {template.sections && (
                  <div style={styles.columns}>
                    <strong>Sections:</strong> {template.sections.join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div style={styles.tabContent} data-testid="content-reports">
          <div style={styles.cardsGrid}>
            {config.reportProfiles.map(profile => (
              <div key={profile.key} style={styles.reportCard}>
                <h4 style={styles.reportName}>{profile.name}</h4>
                <div style={styles.reportMeta}>
                  <span style={styles.typeBadge}>{profile.reportType}</span>
                  <span style={styles.scheduleBadge}>{profile.schedule}</span>
                </div>
                <div style={styles.requiredFields}>
                  <strong>Required Fields:</strong>
                  <div style={styles.fieldsList}>
                    {profile.requiredFields.map(f => (
                      <code key={f} style={styles.fieldCode}>{f}</code>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  notEnabled: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.5rem' },
  tab: { padding: '0.5rem 1rem', borderRadius: '6px 6px 0 0', border: 'none', background: 'transparent', fontSize: '0.875rem', cursor: 'pointer', color: '#718096' },
  activeTab: { padding: '0.5rem 1rem', borderRadius: '6px 6px 0 0', border: 'none', background: '#3182ce', fontSize: '0.875rem', cursor: 'pointer', color: 'white' },
  tabContent: {},
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  shortcutsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' },
  shortcutCard: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', cursor: 'pointer', transition: 'box-shadow 0.15s', textAlign: 'center' },
  shortcutLabel: { fontSize: '0.875rem', fontWeight: '500', color: '#3182ce' },
  summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' },
  summaryCard: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' },
  summaryValue: { display: 'block', fontSize: '2rem', fontWeight: '600', color: '#2d3748' },
  summaryLabel: { fontSize: '0.75rem', color: '#718096', textTransform: 'uppercase' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  code: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '2px', fontSize: '0.75rem' },
  typeBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#bee3f8', color: '#2b6cb0', fontSize: '0.75rem' },
  formatBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#e2e8f0', color: '#4a5568', fontSize: '0.75rem' },
  scheduleBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#c6f6d5', color: '#276749', fontSize: '0.75rem' },
  requiredBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#fed7d7', color: '#c53030', fontSize: '0.75rem' },
  optionalBadge: { padding: '0.25rem 0.5rem', borderRadius: '4px', background: '#e2e8f0', color: '#718096', fontSize: '0.75rem' },
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' },
  templateCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  templateName: { margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  templateMeta: { display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' },
  columns: { fontSize: '0.75rem', color: '#718096' },
  reportCard: { background: 'white', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  reportName: { margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  reportMeta: { display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' },
  requiredFields: { fontSize: '0.75rem', color: '#718096' },
  fieldsList: { display: 'flex', gap: '0.25rem', flexWrap: 'wrap', marginTop: '0.25rem' },
  fieldCode: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '2px', fontSize: '0.7rem' }
};
